import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';

import AppButton from '../../../../components/designe/app-button/AppButton';
import { useStyles } from '../Sync.styles';
import theme from '../../../../styles/theme';
import {
  useGetSyncProviders,
  useLazyGetExercisesForSyncQuery,
  useSyncExerciseMutation,
} from '../../../../services/redux/api/syncApi';
import type {
  ExerciseSyncItemDto,
  SyncExerciseRequestDto,
} from '../../../../services/redux/api/syncApi';
import {
  SyncExerciseListItem,
  type SyncItemStatusDot,
} from './SyncExerciseListItem';
import { SyncExerciseJsonView } from './SyncExerciseJsonView';
import { SyncExerciseForm } from './SyncExerciseForm';
import { useDispatch } from 'react-redux';
import { exercisesApi } from '../../../../services/redux/api/exercisesAPI';
import { EXERCISE_PAGES } from '../../../../services/redux/constants';
import { useGetFilePaths } from '../../../../services/redux/api/appConfigApi';
import {
  uploadFileAndCreateAppFile,
  deleteFilesFromFirebase,
} from '../../../../utils/firebase-storage-utils';
import { IAppFile } from '../../../../types/interfaces/app-file';
import { INewFile } from '../../../../types/interfaces/new-file';
import { useLayout } from '../../../../context/LayoutContext';

export function SyncExercisesContent() {
  const { t } = useTranslation();
  const { classes } = useStyles(theme);
  const dispatch = useDispatch();
  const { showLoader, hideLoader } = useLayout();
  const { data: providers } = useGetSyncProviders();
  const { data: filePaths } = useGetFilePaths();
  const [getExercises, { isFetching }] = useLazyGetExercisesForSyncQuery();
  const [syncExercise, { isLoading: isSaving }] = useSyncExerciseMutation();
  const [isUploadingImages, setIsUploadingImages] = useState(false);

  const [localExercises, setLocalExercises] = useState<ExerciseSyncItemDto[]>(
    [],
  );
  const [selectedItem, setSelectedItem] = useState<ExerciseSyncItemDto | null>(
    null,
  );
  const [formDirty, setFormDirty] = useState(false);

  const exerciseProviders = useMemo(
    () => providers?.filter((p) => p.entities.includes('exercise')) ?? [],
    [providers],
  );
  const firstProvider = exerciseProviders[0]?.provider ?? '';
  const [provider, setProvider] = useState<string>(firstProvider);

  useEffect(() => {
    if (exerciseProviders.length > 0) {
      setProvider((prev) => prev || exerciseProviders[0].provider);
    }
  }, [exerciseProviders]);

  const currentProvider = provider || firstProvider;

  const handleLoad = useCallback(() => {
    if (!currentProvider) return;
    getExercises(currentProvider).then((result) => {
      if (result?.data) {
        setLocalExercises(result.data);
        setSelectedItem(null);
        setFormDirty(false);
      }
    });
  }, [getExercises, currentProvider]);

  const handleSave = useCallback(
    async (
      body: SyncExerciseRequestDto,
      extra?: {
        filesToUpload: INewFile[];
        avatarFileId: string | number | null;
      },
    ) => {
      if (!currentProvider) return;
      const filesToUpload = extra?.filesToUpload ?? [];
      const avatarFileId = extra?.avatarFileId;

      const loaderId = showLoader();
      const uploadedStoragePaths: string[] = [];

      try {
        let filesForCreate: Array<{
          id: string | number;
          name: string;
          mimeType: string;
          storageLink?: string;
          previewStorageLink?: string;
        }> = [];
        let mappedAvatarFileId: string | number | undefined;

        if (filesToUpload.length > 0 && filePaths?.exercises) {
          setIsUploadingImages(true);
          const uploadedFiles: IAppFile[] = await Promise.all(
            filesToUpload.map((file) =>
              uploadFileAndCreateAppFile(file, filePaths.exercises),
            ),
          );
          uploadedFiles.forEach((f) => {
            uploadedStoragePaths.push(f.storageLink);
            if (f.previewStorageLink)
              uploadedStoragePaths.push(f.previewStorageLink);
          });

          filesForCreate = uploadedFiles.map((f) => ({
            id: f.id ?? '',
            name: f.name,
            mimeType: f.mimeType,
            storageLink: f.storageLink,
            previewStorageLink: f.previewStorageLink,
          }));

          if (avatarFileId != null) {
            const idx = filesToUpload.findIndex(
              (f) => String(f.id) === String(avatarFileId),
            );
            if (idx >= 0 && uploadedFiles[idx])
              mappedAvatarFileId = uploadedFiles[idx].id as string;
          }
          if (mappedAvatarFileId == null && uploadedFiles[0])
            mappedAvatarFileId = uploadedFiles[0].id as string;
        }

        const requestBody: SyncExerciseRequestDto = { ...body };
        if (body.create) {
          requestBody.create = {
            ...body.create,
            filesToUpload:
              filesForCreate.length > 0 ? filesForCreate : undefined,
            avatarFileId:
              filesForCreate.length > 0 ? mappedAvatarFileId : undefined,
          };
        }

        const result = await syncExercise({
          provider: currentProvider,
          body: requestBody,
        }).unwrap();

        dispatch(
          exercisesApi.util.invalidateTags([
            { type: EXERCISE_PAGES.PUBLIC },
            { type: EXERCISE_PAGES.ALL },
          ]),
        );
        setLocalExercises((prev) =>
          prev.map((item) =>
            item.providerResourceId === result.providerResourceId
              ? { ...item, localResourceId: result.localResourceId }
              : item,
          ),
        );
        setSelectedItem(null);
        setFormDirty(false);
      } catch (err) {
        if (uploadedStoragePaths.length > 0) {
          await deleteFilesFromFirebase(uploadedStoragePaths);
        }
        throw err;
      } finally {
        setIsUploadingImages(false);
        hideLoader(loaderId);
      }
    },
    [
      currentProvider,
      syncExercise,
      dispatch,
      filePaths,
      showLoader,
      hideLoader,
    ],
  );

  function getStatusDot(item: ExerciseSyncItemDto): SyncItemStatusDot {
    const isSelected =
      selectedItem?.providerResourceId === item.providerResourceId;
    if (isSelected && formDirty) return 'unsaved';
    return item.localResourceId ? 'synced' : 'notSynced';
  }

  return (
    <div className={classes.wrapper}>
      <div className={classes.toolbar}>
        <FormControl size="small" className={classes.providerSelect}>
          <InputLabel>{t('adminDashboard.sync.provider')}</InputLabel>
          <Select
            value={currentProvider}
            label={t('adminDashboard.sync.provider')}
            onChange={(e) => setProvider(e.target.value)}>
            {exerciseProviders.map((p) => (
              <MenuItem key={p.provider} value={p.provider}>
                {p.provider}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <AppButton
          onClick={handleLoad}
          loader={isFetching}
          disabled={!currentProvider}>
          {t('adminDashboard.sync.load')}
        </AppButton>
      </div>

      <div className={classes.threeColumnLayout}>
        <div className={classes.listColumn}>
          <div className={classes.listWrapper}>
            {localExercises.length === 0 && !isFetching ? (
              <div className={classes.emptyState}>
                {t('adminDashboard.sync.emptyList')}
              </div>
            ) : (
              localExercises.map((item) => (
                <SyncExerciseListItem
                  key={item.providerResourceId}
                  item={item}
                  selected={
                    selectedItem?.providerResourceId === item.providerResourceId
                  }
                  statusDot={getStatusDot(item)}
                  onSelect={() => setSelectedItem(item)}
                />
              ))
            )}
          </div>
        </div>
        <SyncExerciseJsonView item={selectedItem} />
        <SyncExerciseForm
          item={selectedItem}
          provider={currentProvider}
          onSave={handleSave}
          onDirtyChange={setFormDirty}
          isSaving={isSaving || isUploadingImages}
        />
      </div>
    </div>
  );
}
